import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Form, Outlet, replace, useMatch, useOutletContext, useNavigate, useParams, useSubmit } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js'
import './contribution.css'

// COMPONENTS
import * as Icons from '@icons'
import { Column } from '@components/templates/column'
import Avatar from '@components/molecules/avatar'
import CurrencyTextField from '@components/molecules/currency-textfield'
import Button from '@components/atoms/button'
import PaymentMethodCell from '@components/organisims/payment-method-cell'

// ROUTES
import AddCardRoute from './add'

// TYPES
import type { RouteObject } from 'react-router-dom'
import type { StripeExpressCheckoutElementConfirmEvent, Stripe, SetupIntent } from '@stripe/stripe-js'

// UTILITIES
import { max } from '@constants'
import { createResponseObj, StripeAPI, VirtualCardAPI } from '@apis'
import { parseUserLimits } from '@schemas'
import { usePaymentMethods } from '@/hooks/tanstack/usePaymentMethods'
import { isNonEmptyString, isNumber, isObject } from '@utils/type-checkers'
import { createAuthenticatedAction } from '@utils/actions'
import { priceFormatter, toPennies } from '@utils/number-helper'
import { useCurrencyInput } from '@hooks/inputs'
import { refreshClerkUser } from '@utils/loaders'
import { useGroupLoaderData } from '../../..'


type EditPledgePMContext = [{ paymentMethod?: CreditCard, setupIntent?: SetupIntent }, React.Dispatch<React.SetStateAction<{
    paymentMethod?: CreditCard
    setupIntent?: SetupIntent
}>>]
export const useEditPledgePMOutletContext = () => useOutletContext<EditPledgePMContext>()

let stripe: Stripe | null = null

const formId = "edit-pledge-contribution"
const Component: FC<{}> = () => {
    const { user } = useUser()
    const { data: paymentMethods = [] } = usePaymentMethods(user?.id)
    const { pledgeId } = useParams()
    const group = useGroupLoaderData()
    const pledgeToEdit = group.pledges?.find(({ id }) => id === pledgeId)
    const [pledgeAmount, pledgeAmountProps] = useCurrencyInput({
        initValue: `${(pledgeToEdit?.amount || 0) / 100}`,
        max: max,
    })
    const navigate = useNavigate()
    const submitable = useMemo(() => (!!pledgeAmount && toPennies(pledgeAmount) >= 100 && toPennies(pledgeAmount) <= max), [pledgeAmount])
    const feeAmount = 0
    const { groupId } = useParams()
    const submit = useSubmit()

    stripe = useStripe()
    const elements = useElements()

    const isAdd = useMatch({ path: '/group/:groupId/edit/:pledgeId/contribution/add' })
    const [state, setState] = useState<{ paymentMethod?: CreditCard | ApplePay | GooglePay, setupIntent?: SetupIntent }>({})

    const method = useMemo(() => JSON.stringify(state.paymentMethod || ''), [state.paymentMethod])
    const pledge = useMemo(() => JSON.stringify(group.pledges?.find(({ id }) => id === pledgeId) || ''), [group.pledges, pledgeId])
    const currentPaymentMethod = useMemo(() => JSON.parse(pledge).paymentMethod, [pledge])

    const ref = useRef<HTMLDialogElement>(null)
    function openFeeInfo() { if (ref.current) ref.current.showModal() }
    function closeFeeInfo() { if (ref.current) ref.current.close() }

    useEffect(() => {
        if (state.paymentMethod?.type === 'apple_pay' || state.paymentMethod?.type === 'google_pay') {
            const form = document.getElementById(formId) as HTMLFormElement
            submit(form)
        }
    }, [state.paymentMethod?.type])

    async function handleExpress(event: StripeExpressCheckoutElementConfirmEvent) {
        if (!stripe || !elements) throw new Error("Failed to load Stripe");
        await elements.submit()
        const { expressPaymentType } = event
        console.log("On Confirm:", event)

        const { clientSecret } = await StripeAPI.CreateSetupIntentRequest.fetch({ type: expressPaymentType as any })
        const { setupIntent, error } = await stripe.confirmSetup({
            elements, clientSecret, confirmParams: {
                return_url: new URL(`/group/${groupId}`, document.location.href).toString()
            }, redirect: 'if_required'
        })
        if (error) throw error

        setState((currentState) => ({
            ...currentState,
            setupIntent,
            paymentMethod: {
                type: expressPaymentType as any,
                cardType: expressPaymentType as any
            },
        }))
    }

    function handlePMSelected(card: CreditCard) {
        setState((currentState) => ({
            ...currentState,
            paymentMethod: card
        }))
    }

    function handleAdd() {
        navigate('./add')
    }

    function handleBack() {
        navigate(-1)
    }

    return (<>
        <Column.Header.Start>
            {!isAdd && <a onClick={handleBack}>
                <img src="/icons/arrow-left.svg" alt="back" style={{ width: '1.4rem', height: '1.4rem' }} draggable="false" />
            </a>}
        </Column.Header.Start>
        {!isAdd && (
            <Form id={formId} method="POST" className="flex flex-col" style={{ gap: 'inherit' }}>
                <input onChange={() => { }} value={method} name="paymentMethod" hidden />
                <input onChange={() => { }} value={state.setupIntent?.id || ''} name="setupIntentId" hidden />
                <input onChange={() => { }} value={pledge} name="pledge" hidden />
                <h2 style={{ textAlign: 'center' }}>Change Your Share</h2>
                <p className="text-body-2 text-gray-500 text-center">Edit the amount you are contributing towards this purchase.</p>
                <div className="flex flex-row items-center" style={{ width: '100%', padding: '0.625rem 1.25rem', gap: 'inherit', backgroundColor: 'var(--splitcard-gray-50)', border: '1px solid var(--splitcard-gray-100)', borderRadius: 'var(--border-radius-md)' }}>
                    <Avatar user={user} />
                    <p className="text-body-2"><strong>{user?.fullName || 'Me'}</strong></p>
                </div>
                <CurrencyTextField centered={true} {...pledgeAmountProps} name="amount" />
                <div className='flex flex-row items-center gap-4'>
                    <p className="text-body-2" style={{ color: 'var(--splitcard-gray-500)' }}><strong>{priceFormatter.format(feeAmount)}</strong> is your estimated fee</p>
                    <Icons.Info onClick={openFeeInfo} type='filled' height="1rem" width="1rem" style={{ color: 'var(--splitcard-blue-500)', cursor: 'pointer' }} />
                    <dialog ref={ref}>
                        <div>
                            <div className="flex flex-col items-stretch" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem', background: 'var(--splitcard-white)', gap: '1rem', padding: '1.3125rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                                <h2>Splitcard Fee</h2>
                                <p><strong>Good news — we’ve waived our fee!</strong> Splitcard is free to use for a limited time.</p>
                                <p>Now’s the perfect time to try it and <strong>share with friends and family</strong></p>
                                <div className="flex flex-col items-stretch gap-2.5">
                                    <Button theme="primary" formMethod="dialog" onClick={closeFeeInfo}>Got it!</Button>
                                </div>
                            </div>
                        </div>
                    </dialog>
                </div>
            </Form >
        )}
        <h2 style={{ textAlign: 'center' }}>Payment Method</h2>
        {(isAdd || currentPaymentMethod === undefined || currentPaymentMethod?.type === 'credit_card') ? null :
            <PaymentMethodCell
                selected={!state.paymentMethod?.lastFour && currentPaymentMethod.type !== 'credit_card'}
                style={{ borderColor: 'var(--splitcard-blue-500)' }}
                paymentMethod={currentPaymentMethod}
            />
        }
        <PaymentMethodCell type={isAdd ? 'empty' : 'add'} onClick={!isAdd ? handleAdd : undefined}>
            <Outlet context={[state, setState]} />
        </PaymentMethodCell>
        {isAdd ? null : paymentMethods.map((method, index) => (
            <PaymentMethodCell
                key={`${method.token}-${index}`}
                className={method.token === currentPaymentMethod?.token ? '.current-payment-method' : ''}
                style={{ borderColor: method.token === currentPaymentMethod?.token ? 'var(--splitcard-blue-500)' : '' }}
                paymentMethod={method}
                selected={(state.paymentMethod?.type === 'credit_card' && method.token === state.paymentMethod?.token) || (!state.paymentMethod?.lastFour && currentPaymentMethod.type === 'credit_card' && method.token === currentPaymentMethod?.token)}
                onClick={() => handlePMSelected(method)} />
        ))}
        {(!isAdd) && (
            <ExpressCheckoutElement options={{
                layout: { maxColumns: 1, maxRows: 2 },
                paymentMethods: { applePay: 'always', googlePay: 'always' },
            }}
                onConfirm={handleExpress}
                onCancel={(event) => { console.log("On Cancel:", event) }}
                onLoadError={(err) => { console.error("On Load Error:", err) }}
                onReady={(event) => { console.log("On Ready:", event) }}
                onClick={({ resolve }) => {
                    resolve({
                        phoneNumberRequired: true,
                        emailRequired: true,
                    })
                }} />
        )}
        {isAdd ? null : (<>
            <Column.Footer>
                <Button form={formId} type="submit" disabled={!submitable}>Update</Button>
            </Column.Footer>
        </>)}
    </>)
}



const action = createAuthenticatedAction(async ({ request, params }, Clerk) => {
    const formData = await request.formData()
    const groupId = params['groupId']
    const pledgeId = params['pledgeId']
    const amount = toPennies(`${formData.get('amount')}` || '')
    const paymentMethod = `${formData.get('paymentMethod') || ''}`
    const pledge = JSON.parse(`${formData.get('pledge') || ''}`)

    await refreshClerkUser(Clerk)
    const userLimits = parseUserLimits(Clerk.user?.publicMetadata?.limits)

    console.log("Update Pledge Data:", { groupId, pledge, pledgeId, paymentMethod, amount })
    if (!isNonEmptyString(groupId) || !isObject<Pledge>(pledge) || !isNonEmptyString(pledgeId) || (!isNumber(amount) || amount < 100)) throw createResponseObj({
        status: 'failed', type: 'local', name: 'missing-resources',
        message: `Missing required resources. ${JSON.stringify({ groupId, pledge, paymentMethod })}`
    })

    if (!userLimits.addAdditionalFunds && amount > (pledge.amount || 0)) {
        return replace(`/group/${groupId}`)
    }

    await VirtualCardAPI.UpdatePledgeRequest.fetch(groupId, { id: pledgeId, amount })

    if (!!paymentMethod) {
        const _paymentMethod = JSON.parse(`${formData.get('paymentMethod') || ''}`)

        if (isObject<CreditCard>(_paymentMethod)) {
            var setupIntentId = formData.get('setupIntentId') as string
            if (!isNonEmptyString(setupIntentId)) {
                const { clientSecret } = await StripeAPI.CreateSetupIntentRequest.fetch()
                const { setupIntent, error } = await stripe?.confirmCardSetup(clientSecret, {
                    payment_method: _paymentMethod.token
                }) || {}

                if (!setupIntent || error) throw error || new Error("Failed to generate Setup intent")
                setupIntentId = setupIntent.id
            }

            await VirtualCardAPI.UpdatePledgeRequest.fetch(groupId, {
                ...pledge,
                id: pledge.id!,
                transactionId: setupIntentId,
                paymentMethod: _paymentMethod,
            })
        }
    }

    return replace(`/group/${groupId}`)
})

const EditPledgeContributionRoute: RouteObject = {
    path: 'contribution',
    Component,
    action,
    children: [
        AddCardRoute,
    ],
}

export { EditPledgeContributionRoute }
